import {Component, EventEmitter, OnInit, Optional, Output} from '@angular/core';
import { FormElementModel } from '../../../ui-kit/components/ui-form/form-element';
import { AppStore } from '../../../../stores/app-store';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { IUser } from '../../../../interfaces';
import { SelectedWorkspaceStore } from '../../../../stores/selected-workspace.store';
import { SettingsService } from '../../../services/api/settings.service';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { BoardsApiService } from '../../../services/api/boards-api.service';
import { firstValueFrom } from 'rxjs';
import { SaveWelcomeBoardService } from '../../../services/save-welcome-board.service';
import { OnboardingService } from '../../../services/onboarding.service';
import {DialogRef} from "@angular/cdk/dialog";
import { throwError } from 'rxjs';
import { MetricService } from '../../../../core/services/metric.service';
import { UserJourneyEvent } from '../../../../core/services/user-journey.types';
import { TranslateService } from '@ngx-translate/core';

export type FormValues = {
  name: string;
  phone: string;
  company: string;
  job: string;
};
@Component({
  selector: 'app-user-additional-form',
  templateUrl: './user-additional-form.component.html',
  styleUrls: ['./user-additional-form.component.less'],
})
export class UserAdditionalFormComponent implements OnInit {
  @Output() submitSuccess = new EventEmitter<void>();
  public requiredValidator = (v: any) => (!v ? 'Required' : null);
  private destroy$ = new EventEmitter<void>();
  public isSubmitting = false;

  public formState = {
    name: new FormElementModel(''),
    phone: new FormElementModel(''),
    company: new FormElementModel(''),
    job: new FormElementModel(''),
  };

  public user: IUser | null = null;

  public roleOptions: { label: string; value: string }[] = [];

  constructor(
    private settingsService: SettingsService,
    private store: AppStore,
    private router: Router,
    private workspaceStore: SelectedWorkspaceStore,
    private errorHandler: ErrorHandlerService,
    private boardsApi: BoardsApiService,
    private saveWelcomeBoard: SaveWelcomeBoardService,
    private onboardingService: OnboardingService,
    @Optional() private dialogRef: DialogRef,
    private metricService: MetricService,
    private translate: TranslateService,
  ) {
    this.user = this.workspaceStore.getUserSync();
  }

  ngOnInit() {
    // Если форма открыта в диалоге (на карте) и уже показывалась в этой сессии — сразу закрываем.
    if (this.dialogRef && sessionStorage.getItem('additional-form-showed') === '1') {
      this.dialogRef.close();
      return;
    }
    this.formState = {
      name: new FormElementModel(this.user?.name || ''),
      phone: new FormElementModel(this.user?.phone || ''),
      company: new FormElementModel(this.user?.company || ''),
      job: new FormElementModel(this.user?.role || ''),
    };

    this.roleOptions = [
      { value: 'Agent', label: this.translate.instant('role_group.agent') },
      { value: 'Agriculture', label: this.translate.instant('role_group.agriculture') },
      { value: 'Analyst', label: this.translate.instant('role_group.analyst') },
      { value: 'Appraiser', label: this.translate.instant('role_group.appraiser') },
      { value: 'Consultant', label: this.translate.instant('role_group.consultant') },
      { value: 'Developer', label: this.translate.instant('role_group.developer') },
      { value: 'Employee', label: this.translate.instant('role_group.employee') },
      { value: 'Entrepreneur', label: this.translate.instant('role_group.entrepreneur') },
      { value: 'Executive (Management)', label: this.translate.instant('role_group.executive_management') },
      { value: 'Finance', label: this.translate.instant('role_group.finance') },
      { value: 'Freelancer', label: this.translate.instant('role_group.freelancer') },
      { value: 'Government', label: this.translate.instant('role_group.government') },
      { value: 'Healthcare (Medical)', label: this.translate.instant('role_group.healthcare_medical') },
      { value: 'Homemaker', label: this.translate.instant('role_group.homemaker') },
      { value: 'Investor', label: this.translate.instant('role_group.investor') },
      { value: 'IT', label: this.translate.instant('role_group.it') },
      { value: 'Legal', label: this.translate.instant('role_group.legal') },
      { value: 'Other', label: this.translate.instant('role_group.other') },
      { value: 'Owner', label: this.translate.instant('role_group.owner') },
      { value: 'Sales', label: this.translate.instant('role_group.sales') },
      { value: 'Student', label: this.translate.instant('role_group.student') },
      { value: 'Teacher', label: this.translate.instant('role_group.teacher') },
      { value: 'Unemployed', label: this.translate.instant('role_group.unemployed') },
    ];
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public submit(event: any) {
    const formValues = event as FormValues;
    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.metricService.track({ name: 'registration_additional-info_submit' });
    this.metricService.track({ name: UserJourneyEvent.SIGN_UP_COMPLETE });
    this.metricService.track({ name: UserJourneyEvent.FIRST_LOGIN });
    this.settingsService
      .updateUser({
        name: formValues.name,
        phone: formValues.phone,
        company: formValues.company,
        role: formValues.job,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        async (it) => {
          this.workspaceStore.setUser(it);
          if (this.dialogRef) {
            sessionStorage.setItem('additional-form-showed', '1');
          }
          if (this.saveWelcomeBoard.isWelcomeBoardElementsSaved()) {
            // TODO REG FIX
            if (!this.dialogRef) {
              this.saveWelcomeBoard
                .createWelcomeBoardElementsFromLS()
                .pipe()
                .subscribe((it) => {
                  if (it) {
                    this.router.navigate(['board', it.boardId]);
                  } else {
                    this.router.navigate(['projects']);
                  }
                });
            }

            this.onboardingService.onUserLoggedIn();
            this.submitSuccess.emit();
            if (this.dialogRef) {
              this.dialogRef.close();
            }
            this.isSubmitting = false;
            return;
          }

          this.router.navigate(['/projects']);

          this.submitSuccess.emit();
          if (this.dialogRef) {
            this.dialogRef.close();
          }
          this.isSubmitting = false;
        },
        (err) => {
          this.errorHandler.handleError(err);
          this.isSubmitting = false;
          return throwError(() => err);
        },
      );
  }
}
